// 261010 P10 F1 (S1) — ô tuỳ chọn «Dựng ảnh từ nhân vật» trong phần Sửa / Chi tiết của thẻ nhân vật PERSON.
// Chọn gốc ⇒ hiện «Lúc khác tuổi / Hình thái khác» + «Mô tả». «— Không —» ⇒ gửi `identity: null`. Không đụng ô ⇒ không gửi gì. Logic ở `characterIdentityUtils.ts` (thuần, có test).
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Select } from 'antd';
import type { FilmCharacter, FilmCharacterIdentityInput, IdentityVariantKind } from '../../types/film';
import {
  IDENTITY_KIND_LABEL, IDENTITY_NONE_LABEL, MAX_IDENTITY_DESCRIPTION, buildIdentityPatch, defaultIdentityDescription,
  identityBaseOptions, identityDraftOf, identityLockedNote, type IdentityDraft,
} from './characterIdentityUtils';

const NONE = '__none__';

interface Props {
  character: FilmCharacter;
  /** Mọi nhân vật của truyện (nguồn danh sách gốc). */
  storyCharacters: FilmCharacter[];
  /** Gửi lên BE. Trả `true` = đã lưu; `false` = lỗi (thông báo đã hiện) ⇒ form GIỮ nguyên nháp. */
  onSave: (c: FilmCharacter, identity: FilmCharacterIdentityInput | null) => Promise<boolean>;
}

const CharacterIdentityField: React.FC<Props> = ({ character: c, storyCharacters, onSave }) => {
  // Khoá theo liên kết đang có: lưu xong / dữ liệu tải lại ⇒ nháp quay về đúng liên kết mới.
  const sig = `${c.id}|${c.identityBaseCharacterId || ''}|${c.identityVariantKind || ''}|${c.identityVariantDescription || ''}`;
  const [draft, setDraft] = useState<IdentityDraft>(() => identityDraftOf(c));
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setDraft(identityDraftOf(c)); setErr(null); }, [sig]);

  const options = useMemo(() => identityBaseOptions(storyCharacters, c), [storyCharacters, c]);
  const locked = identityLockedNote(storyCharacters, c);
  const patch = buildIdentityPatch(c, draft);
  const dirty = patch.mode !== 'skip';

  const pickBase = (v: string) => {
    setErr(null);
    if (v === NONE) { setDraft((d) => ({ ...d, baseId: null })); return; }
    const base = storyCharacters.find((x) => x.id === v);
    setDraft((d) => ({
      ...d, baseId: v,
      description: d.description.trim() ? d.description : defaultIdentityDescription(c, base?.name || '', d.kind),
    }));
  };
  const pickKind = (kind: IdentityVariantKind) => { setErr(null); setDraft((d) => ({ ...d, kind })); };

  const submit = async () => {
    const p = buildIdentityPatch(c, draft);
    if (p.mode === 'error') { setErr(p.error); return; }
    if (p.mode === 'skip') return;
    setSaving(true);
    try { await onSave(c, p.identity); } finally { setSaving(false); }
  };

  if (locked) return <div className="bk-dimline" data-testid="bk-identity-locked">{locked}</div>;
  // Tên gốc hiện tại không còn trong danh sách (hiếm: gốc đã bị xoá) ⇒ vẫn hiện được để người ta chọn lại / bỏ.
  const value = draft.baseId && options.some((o) => o.value === draft.baseId) ? draft.baseId : (draft.baseId ? undefined : NONE);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-testid="bk-identity-field">
      <Select size="small" style={{ width: '100%' }} value={value} placeholder="Chọn nhân vật gốc" showSearch optionFilterProp="label"
        onChange={pickBase} options={[{ value: NONE, label: IDENTITY_NONE_LABEL }, ...options]} />
      {draft.baseId && (
        <>
          <Select size="small" style={{ width: '100%' }} value={draft.kind} onChange={pickKind}
            options={(['AGE', 'FORM'] as IdentityVariantKind[]).map((k) => ({ value: k, label: IDENTITY_KIND_LABEL[k] }))} />
          <Input.TextArea size="small" autoSize={{ minRows: 1, maxRows: 3 }} maxLength={MAX_IDENTITY_DESCRIPTION} value={draft.description}
            placeholder="Mô tả, vd: Mira lúc 6 tuổi · Mira hoá sói trắng" status={err ? 'error' : undefined}
            onChange={(e) => { setErr(null); setDraft((d) => ({ ...d, description: e.target.value })); }} />
        </>
      )}
      {err && <div className="bk-hint warn" role="alert">{err}</div>}
      {dirty && (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => { setDraft(identityDraftOf(c)); setErr(null); }}>Thôi</Button>
          <Button size="small" type="primary" loading={saving} onClick={submit}>Lưu</Button>
        </div>
      )}
      {!draft.baseId && !dirty && <div className="bk-dimline" style={{ marginTop: 0 }}>Để trống nếu ảnh nhân vật này không cần dựng từ nhân vật khác.</div>}
    </div>
  );
};

export default CharacterIdentityField;
